<template>
  <div
    ref="dot"
    :class="{
      'is-active': isActive,
      'ps-is-mine': isMine,
      'is-create-mode': isCreateMode,
      'is-hidden': activeId && !isActive
    }"
    class="picnic_team_dot"
    @click.stop="launchCard"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    :data-picnic-id="id"
    :aria-label="title"
    :style="style"
  >
    <div class="ps-glow"></div>
    <the-notation
      v-if="isActive || isCreateMode"
      :type="dot.notation"
      :selector="selector"
    />
  </div>
</template>

<script>
import { mapActions } from '@picnic-software/vuex'
import scrollIntoView from 'scroll-into-view'
import Popper from 'popper.js'
import { getEl } from '@/utils/dot-helpers'
import TheNotation from '@/components/TheNotation'

import { ROUTE_CARD_PEEK, ROUTE_CARD_DETAILS } from '@/common/constants'

export default {
  name: 'TheDot',
  props: {
    dot: {
      type: Object,
      default: () => {}
    },
    isCreateMode: {
      type: Boolean,
      default: false
    },
    activeId: {
      type: [Number, String]
    },
    activeRoute: {
      type: String
    },
    isVisible: {
      type: Boolean,
      default: false
    },
    providerUserId: {
      type: String,
      default: null
    }
  },
  components: {
    TheNotation
  },
  data() {
    return {
      style: {
        left: null,
        top: null,
        transform: null,
        position: 'absolute'
      },
      transform: null,
      scale: 0,
      popperOffsetLeft: null,
      isOrphaned: true
    }
  },
  computed: {
    selector() {
      if (Array.isArray(this.dot.selector) && this.dot.selector.length) {
        return this.dot.selector[0]
      }
      return this.dot.selector
    },
    id() {
      return this.dot.id
    },
    providerCardId() {
      return this.dot.provider_card_id
    },
    tether() {
      return this.dot.position.tether
    },
    isReady() {
      return this.selector && this.tether && this.pageEl
    },
    isPeek() {
      return this.activeRoute === ROUTE_CARD_PEEK
    },
    isMine() {
      return this.dot.provider_assigned_to_user_id === this.providerUserId
    },
    isActive() {
      if (this.activeId) {
        return this.activeId === this.id || this.activeId === this.id.toString()
      }
    },
    title() {
      return 'Tap to view card'
    }
  },
  watch: {
    isActive(val) {
      console.log('isActive', val)
      if (val) {
        if (this.isReady) {
          if (this.isPeek) return
          this.activateTicket()
        }
      } else {
        if (this.activeId) {
          this.setScale(0)
        } else {
          this.setScale(1)
        }
      }
    },
    isVisible(val) {
      if (val) return this.setScale(1)
      this.setScale(0)
    }
  },
  async mounted() {
    this.applyPosition()
    await this.$nextTick()
    this.pop()
    await this.$nextTick()
    if (this.isCreateMode) {
      return this.setScale(0.5)
    }
    if (this.isActive) {
      return this.activateTicket()
    }
    this.setScale(1)
  },
  async beforeDestroy() {
    await this.setScale(0)
  },
  methods: {
    ...mapActions({
      routerControls: 'router/controls',
      routerCard: 'router/card'
    }),
    pop() {
      console.log('dot selector', this.selector)
      this.pageEl = getEl(this.selector)
      if (!this.pageEl) return
      this.isOrphaned = false
      this.popper = new Popper(
        this.pageEl,
        this.$refs.dot,
        this.popperOptions()
      )
    },
    applyDotStyle(data) {
      this.popperOffsetLeft = data.offsets.popper.left
      this.transform = data.styles.transform
      this.style.left = data.styles.left
      this.style.top = data.styles.top
      this.style.transform = this.applyTransform(data.styles.transform)
    },
    applyPosition() {
      if (this.tether.isFixed) {
        this.style.position = 'fixed'
      }
    },
    applyTransform(transform) {
      return this.$nextTick(async () => {
        this.style.transform = `${transform || this.transform} scale(${
          this.scale
        })`
        return
      })
    },
    setScale(scale = 1) {
      this.scale = scale
      return this.applyTransform()
    },
    async activateTicket() {
      this.setScale(0.5)
      await this.$nextTick()
      setTimeout(() => {
        scrollIntoView(this.pageEl)
      }, 400)
    },
    async launchCard(e) {
      clearTimeout(this.peekTimeout)
      await this.routerCard({
        id: this.id,
        offsetLeft: this.popperOffsetLeft,
        tab: ROUTE_CARD_DETAILS
      })
      this.setScale(0.5)
    },
    handleMouseEnter() {
      if (this.isActive || this.isCreateMode) return

      this.setScale(2.5)
      this.peekTimeout = setTimeout(() => {
        this.peek()
      }, 450)
    },
    handleMouseLeave() {
      if (this.isCreateMode) return
      if (this.isActive && !this.isPeek) return
      if (!this.isCreateMode && !this.isActive) {
        this.setScale(1)
      }
      clearTimeout(this.peekTimeout)
      setTimeout(() => {
        this.routerControls()
      }, 250)
    },
    peek() {
      this.routerCard({
        id: this.id,
        offsetLeft: this.popperOffsetLeft,
        tab: ROUTE_CARD_PEEK
      })
    },
    popperOptions() {
      return {
        placement: 'top-start',
        positionFixed: this.tether.isFixed,
        modifiers: {
          applyStyle: { enabled: false },
          applyDotStyle: {
            enabled: true,
            fn: this.applyDotStyle,
            order: 900
          },
          flip: {
            enabled: false
          },
          offset: {
            offset: `${this.tether.x}%, -${this.tether.y}%`
          }
        }
      }
    }
  }
}
</script>

<style lang="css">
.picnic_team_dot {
  @apply bg-brand-orange !important;
  width: 16px;
  height: 16px;
  margin-left: -8px;
  box-shadow: 1px 1px 2px 0px rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  color: #fff;
  font-weight: bold;
  font-size: 13px;
  text-align: center;
  line-height: 30px;
  transform: scale(0);
  will-change: transform;
  user-select: none;
  cursor: pointer;
  will-change: transform !important;
  transform-origin: 50% 50%;
  transition: transform 150ms ease-in !important;
  opacity: 0.6 !important;
  z-index: 50000001;

  & .ps-glow {
    width: 16px;
    height: 16px;
    box-shadow: 1px 1px 2px 0px rgba(0, 0, 0, 0.6);
    border-radius: 50%;
  }

  @media $tabletMax {
    width: 30px !important;
    height: 30px !important;
  }

  /**
   * User Assigned Tickets
   */
  &.ps-is-mine {
    opacity: 0.6;
    /* border: 2px solid #fff; */
    box-shadow: 0 0 0 2px rgba(#95d6f2, 0.8);
    animation: picnic-marker-pulse 4s infinite;
    &:hover {
      animation: none;
    }
  }

  &.is-active {
    /* transform: scale(2.8) !important; */
    background-color: $colorBrandOrange !important;
    opacity: 1 !important;
  }

  &.is-create-mode {
    box-shadow: 0 0 0 7px rgba(#7fdbff, 0.4) !important;
    animation: picnic-marker-create-pulse 4s infinite;
    opacity: 1 !important;
  }

  &:hover {
    transition: transform 300ms ease-out !important;
    /* opacity: 1 !important; */
  }
}
</style>
